<template>
  <div
    class="be-stay__tabs hide-scroll"
    :class="{ 'be-stay__elevated': isElevated, 'be-stay__tabs--standalone': isStandalone }">
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <div class="d-flex">
          <div class="be-stay__tab" v-for="i in 2" :key="i">
            <b-skeleton width="80px" height="20px" />
          </div>
        </div>
      </template>
      <template #default>
        <div
          class="be-stay__tab"
          :class="{ 'be-stay__tab--active': selectedTab.id === tab.id }"
          v-for="tab in tabList"
          :key="tab.id"
          @click="selectTab(tab)">
          <template v-if="tab.id === 'room'">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 18v-6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6M3 18v2M21 18v2M3 14h18M7 10V8a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </template>
          <template v-else>
            <img v-if="tab.image_url" :src="tab.image_url" height="18" alt="" />
            <span v-else class="be-stay__tab-icon">
              <HotelProductTabIcon :module="tab.module" :is-active="selectedTab.id === tab.id" />
            </span>
          </template>
          <span class="ml-2">{{ tab.name }}</span>
        </div>
      </template>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { get } from '@/services/index.js';
import HotelProductTabIcon from '@/components/hotel/HotelProductTabIcon.vue';

export default {
  name: 'BeStayTabs',
  components: {
    HotelProductTabIcon,
  },
  props: {
    isElevated: {
      type: Boolean,
      default: false,
    },
    isStandalone: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      tabList: [],
      selectedTab: {},
    };
  },
  computed: {
    ...mapGetters(['getMerchant', 'getActiveHotelModule']),
    // Only SEARCH_ROOM merchants sell additional services from the search page --
    // the others upsell on the cart page, so their tabs would lead nowhere.
    isAdditionalModuleAllowed() {
      return this.getMerchant.additional_appearance === 'SEARCH_ROOM';
    },
  },
  mounted() {
    if (!this.isAdditionalModuleAllowed) {
      this.assignTabList([]);
      this.isLoading = false;
      return;
    }
    this.getCategoryList();
  },
  methods: {
    ...mapMutations(['setActiveHotelModule', 'setShowAdditionalModuleInSearch']),
    async getCategoryList() {
      try {
        this.isLoading = true;
        const apiParams = { merchant_id: this.getMerchant.id };
        const apiResponse = await get('CORE_HOTEL', 'hotel-additional-service-category', apiParams);

        const { success, data = [], message = '' } = apiResponse.data;
        if (!success) throw new Error(message);
        this.assignTabList(data || []);
      } catch (error) {
        // Keep at least the Room tab so the bar never disappears in v3.
        this.assignTabList([]);
        const errorMsg = error.message || 'An unexpected error occurred';
        console.log('ERROR BeStayTabs getCategoryList :', errorMsg);
      } finally {
        this.isLoading = false;
      }
    },
    assignTabList(additionalCategories) {
      const roomTab = { id: 'room', name: this.$t('hotel.rooms'), module: 'ROOM' };
      this.tabList = [roomTab];

      if (additionalCategories.length > 0) {
        this.tabList.push(...this.parsingDetailModule(additionalCategories));
        this.setShowAdditionalModuleInSearch(true);
      } else {
        this.setShowAdditionalModuleInSearch(false);
      }

      // activeHotelModule is persisted and shared with the concierge pages, so the
      // stored module can be one this page has no tab for.
      const activeModule = this.getActiveHotelModule || {};
      const storedTab = this.tabList.find((tab) => tab.id === activeModule.id);
      this.selectTab(storedTab || roomTab);
    },
    parsingDetailModule(categoryList) {
      return categoryList.map((category) => {
        category.total_additional_service = 0;

        const isModuleActivity =
          this.$isNotEmpty(category.merchant_pos) &&
          (category.merchant_pos.pos_type == 'SPA' || category.merchant_pos.pos_type == 'ACTIVITY');

        category.total_product_display =
          this.$isNotEmpty(category.merchant_pos) &&
          (isModuleActivity || category.merchant_pos.pos_type == 'VOUCHER')
            ? 2
            : 6;

        if (category.merchant_pos == null) category.page_module = 'ORIGINAL';
        else if (isModuleActivity) category.page_module = 'ACTIVITY';
        else category.page_module = category.merchant_pos.pos_type;

        if (category.merchant_pos == null) {
          category.module = 'OTHER';
          category.title = category.name;
          category.subtitle = category.description;
        } else {
          category.module = category.merchant_pos.pos_type;
          category.title = category.merchant_pos.tagline;
          category.subtitle = category.merchant_pos.sub_tagline;
        }

        return category;
      });
    },
    selectTab(tab) {
      const copyTab = this.$deepCopy(tab);
      this.selectedTab = copyTab;
      this.setActiveHotelModule(copyTab);
    },
  },
};
</script>

<style scoped>
.be-stay__tabs {
  display: flex;
  align-items: center;
  border: 1px solid #eee;
  border-radius: var(--be-v3-radius, 10px) var(--be-v3-radius, 10px) 0 0;
  border-bottom: none;
  padding: 4px 8px;
  background: #fff;
  overflow-x: auto;
}
.be-stay__tab {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #444;
  font-weight: 600;
  font-size: 15px;
  border-bottom: 2px solid transparent;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out),
    border-color var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out),
    background-color var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out),
    transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-stay__tab:hover:not(.be-stay__tab--active) {
  background-color: #f5f6f8;
}
.be-stay__tab:active {
  transform: scale(0.98);
}
.be-stay__tab--active {
  color: var(--merchant-primary-color, #79a33a);
  border-bottom-color: var(--merchant-primary-color, #79a33a);
}
.be-stay__elevated {
  position: relative;
  z-index: 2001;
}
.be-stay__tabs--standalone {
  border-radius: var(--be-v3-radius, 10px);
  border-bottom: 1px solid #eee;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}
/* Jost (the v3 default content font) reads heavier at 600 than BeV3Sans did at the
   same weight, so tabs drop to Regular when no custom font is set. */
.be-v3-content-font-default .be-stay__tab {
  font-weight: 400;
}
.be-stay__tab-icon {
  display: inline-flex;
  align-items: center;
}
/* HotelProductTabIcon ships a full-width, 26px-tall block wrapper meant for a
   stacked icon-over-label layout; neutralize it so the icon sits inline here. */
.be-stay__tab-icon >>> div {
  margin: 0 !important;
  width: auto !important;
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeStayTabs.vue